import { Button } from "@games-monorepo/shared-ui";
import {
  DEFAULT_YAK_TRAILS_CONFIG,
  type SessionApi,
  type TournamentAdminAction,
  type TournamentAdminDetails,
  type TournamentDefinition,
  type TournamentSummary,
} from "@games-monorepo/yak-trails-core";
import { useCallback, useEffect, useMemo, useState } from "react";

interface TournamentAdminConsoleProps {
  sessionApi: SessionApi;
  onBack: () => void;
  onChanged: () => void;
}

function defaultStartValue(): string {
  const date = new Date(Date.now() + 24 * 60 * 60_000);
  date.setSeconds(0, 0);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function actionsFor(tournament: TournamentSummary): TournamentAdminAction[] {
  const common: TournamentAdminAction[] = ["validate", "clone"];
  if (tournament.status === "draft") return ["validate", "publish", "reschedule", "clone", "cancel"];
  if (tournament.status === "published") return ["start", "pause", "reschedule", ...common, "cancel"];
  if (tournament.status === "active") return ["pause", "complete", ...common, "cancel"];
  if (tournament.status === "paused") return ["resume", "reschedule", ...common, "cancel"];
  return common;
}

function actionLabel(action: TournamentAdminAction): string {
  return action.replaceAll("_", " ").replace(/^./, (value) => value.toUpperCase());
}

export function TournamentAdminConsole({ sessionApi, onBack, onChanged }: TournamentAdminConsoleProps) {
  const [tab, setTab] = useState<"schedule" | "create">("schedule");
  const [tournaments, setTournaments] = useState<TournamentSummary[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [details, setDetails] = useState<TournamentAdminDetails>();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>();
  const [reason, setReason] = useState("Tournament operations update");
  const [overrideIds, setOverrideIds] = useState("");
  const [name, setName] = useState("Mint Meadow Cup");
  const [startValue, setStartValue] = useState(defaultStartValue);
  const [mode, setMode] = useState<"ffa" | "team">("ffa");
  const [scope, setScope] = useState<"everyone" | "school">("everyone");
  const [respawnPolicy, setRespawnPolicy] = useState<"elimination" | "immediate" | "delayed">("elimination");
  const [schoolIds, setSchoolIds] = useState("");
  const [advancedJson, setAdvancedJson] = useState("");

  const load = useCallback(async (preferredId?: string) => {
    setLoading(true);
    try {
      const available = await sessionApi.listAdminTournaments();
      setTournaments(available);
      const nextId = preferredId || selectedId || available[0]?.id || "";
      setSelectedId(nextId);
      setDetails(nextId ? await sessionApi.getTournamentAdminDetails(nextId) : undefined);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Tournament operations could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [selectedId, sessionApi]);

  useEffect(() => {
    void load();
  // The console intentionally loads once; later refreshes are explicit operations.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visualDefinition = useMemo<Partial<TournamentDefinition>>(() => {
    const startsAtMs = new Date(startValue).getTime();
    const allowedSchoolIds = schoolIds.split(",").map((value) => value.trim()).filter(Boolean);
    const teamStrategy = mode === "team" && scope === "school" && allowedSchoolIds.length === 2
      ? "school_vs_school"
      : mode === "team" && scope === "school" && allowedSchoolIds.length === 1
        ? "within_school"
        : "balanced";
    return {
      id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 64),
      name,
      registrationOpensAtMs: startsAtMs - 7 * 24 * 60 * 60_000,
      registrationClosesAtMs: startsAtMs + 5 * 60_000,
      maxEntrants: teamStrategy === "school_vs_school"
        ? DEFAULT_YAK_TRAILS_CONFIG.tournament.entrantsPerPartition
        : 1_024,
      stages: [{
        id: "qualifier",
        name: "Qualifier",
        order: 0,
        mode,
        scope,
        ...(allowedSchoolIds.length > 0 ? { allowedSchoolIds } : {}),
        teamStrategy,
        rulesetVersion: DEFAULT_YAK_TRAILS_CONFIG.rulesetVersion,
        configVersion: DEFAULT_YAK_TRAILS_CONFIG.configVersion,
        roomCapacity: DEFAULT_YAK_TRAILS_CONFIG.world.capacity,
        botCount: 7,
        checkInOpensAtMs: startsAtMs - DEFAULT_YAK_TRAILS_CONFIG.tournament.defaultCheckInWindowMs,
        startsAtMs,
        durationMs: 10 * 60_000,
        lateJoinWindowMs: 60_000,
        respawnPolicy,
        ...(respawnPolicy === "delayed"
          ? { respawnDelayMs: DEFAULT_YAK_TRAILS_CONFIG.tournament.defaultRespawnDelayMs }
          : {}),
        scoring: mode === "team" ? { type: "team_total" } : { type: "peak_territory" },
        seeding: { type: "random", seed: `${name}:${startsAtMs}` },
        advancement: mode === "team" ? { type: "team_top_k", count: 1 } : { type: "winner" },
        tieBreakers: ["peak_territory", "survival_time", "eliminations", "seed"],
      }],
    };
  }, [mode, name, respawnPolicy, schoolIds, scope, startValue]);

  const resolvedDefinition = (): Partial<TournamentDefinition> => {
    if (!advancedJson.trim()) return visualDefinition;
    const parsed = JSON.parse(advancedJson) as Partial<TournamentDefinition>;
    return { ...parsed, id: parsed.id ?? visualDefinition.id, name: parsed.name ?? name };
  };

  const inspect = async (tournamentId: string) => {
    setSelectedId(tournamentId);
    setLoading(true);
    try {
      setDetails(await sessionApi.getTournamentAdminDetails(tournamentId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Tournament details are unavailable.");
    } finally {
      setLoading(false);
    }
  };

  const mutate = async (tournament: TournamentSummary, action: TournamentAdminAction) => {
    setLoading(true);
    setMessage(undefined);
    try {
      const scheduledAt = action === "reschedule" ? new Date(startValue).toISOString() : undefined;
      await sessionApi.mutateTournament(tournament.id, action, reason, scheduledAt);
      setMessage(`${actionLabel(action)} completed.`);
      await load(tournament.id);
      onChanged();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${actionLabel(action)} failed.`);
      setLoading(false);
    }
  };

  const preview = async () => {
    setLoading(true);
    try {
      const result = await sessionApi.previewTournamentDefinition(resolvedDefinition(), 1_000) as {
        valid?: boolean;
        preview?: { stageCount?: number; stages?: unknown[] };
        errors?: Array<{ path: string; message: string }>;
      };
      setMessage(result.valid
        ? `Valid: ${result.preview?.stageCount ?? result.preview?.stages?.length ?? 0} stage definition.`
        : result.errors?.map((error) => `${error.path}: ${error.message}`).join(" • ") ?? "Definition is invalid.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Definition preview failed.");
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    setLoading(true);
    try {
      const created = await sessionApi.createTournamentDraft(resolvedDefinition(), reason);
      setMessage("Draft created and versioned.");
      setTab("schedule");
      await load(created.id);
      onChanged();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Draft creation failed.");
      setLoading(false);
    }
  };

  const overrideStage = details?.tournament.definition.stages.find(
    (stage) => stage.advancement.type === "staff_override" && stage.nextStageId,
  );
  const selectedSummary = tournaments.find((tournament) => tournament.id === selectedId);

  return (
    <div className="yak-trails__screen yak-trails__admin-screen">
      <header className="yak-trails__screen-header">
        <button type="button" className="yak-trails__icon-button" onClick={onBack} aria-label="Go back">←</button>
        <h2>Tournament Ops</h2>
        <button type="button" className="yak-trails__admin-refresh" onClick={() => void load(selectedId)}>↻</button>
      </header>
      <div className="yak-trails__admin-tabs" role="tablist">
        <button type="button" className={tab === "schedule" ? "is-selected" : ""} onClick={() => setTab("schedule")}>Schedule</button>
        <button type="button" className={tab === "create" ? "is-selected" : ""} onClick={() => setTab("create")}>Create</button>
      </div>

      <div className="yak-trails__admin-scroll">
        {tab === "schedule" ? (
          <>
            <div className="yak-trails__admin-list" aria-label="Tournament schedule">
              {tournaments.map((tournament) => (
                <button
                  type="button"
                  className={tournament.id === selectedId ? "is-selected" : ""}
                  onClick={() => void inspect(tournament.id)}
                  key={tournament.id}
                >
                  <span className={`is-${tournament.status}`} />
                  <b>{tournament.name}</b>
                  <small>{tournament.status} • v{details?.tournament.id === tournament.id ? details.tournament.version : "—"}</small>
                </button>
              ))}
            </div>
            {selectedSummary && details && (
              <section className="yak-trails__admin-card">
                <header><div><small>SELECTED TOURNAMENT</small><h3>{selectedSummary.name}</h3></div><strong>{selectedSummary.status}</strong></header>
                <label>Audit reason<input value={reason} onChange={(event) => setReason(event.target.value)} /></label>
                <div className="yak-trails__admin-actions">
                  {actionsFor(selectedSummary).map((action) => (
                    <span className={action === "cancel" ? "yak-trails__admin-danger" : undefined} key={action}>
                      <Button
                        variant={action === "publish" || action === "start" ? "primary" : "secondary"}
                        disabled={loading || reason.trim().length < 3}
                        onClick={() => void mutate(selectedSummary, action)}
                      >
                        {actionLabel(action)}
                      </Button>
                    </span>
                  ))}
                </div>
                <div className="yak-trails__admin-meta">
                  <span><b>{details.tournament.definition.stages.length}</b> stages</span>
                  <span><b>{details.versions.length}</b> versions</span>
                  <span><b>{details.audit.length}</b> audit events</span>
                </div>
                {overrideStage && (
                  <div className="yak-trails__override-card">
                    <strong>Manual advancement</strong>
                    <p>{overrideStage.name} → {overrideStage.nextStageId}</p>
                    <label>Entrant IDs<input value={overrideIds} onChange={(event) => setOverrideIds(event.target.value)} placeholder="player-1, player-2" /></label>
                    <Button disabled={loading || !overrideIds.trim()} onClick={() => void (async () => {
                      const entrantIds = overrideIds.split(",").map((value) => value.trim()).filter(Boolean);
                      try {
                        const result = await sessionApi.overrideTournamentAdvancement(
                          selectedSummary.id,
                          overrideStage.id,
                          entrantIds,
                          reason,
                        );
                        setMessage(`${result.advanced} entrant${result.advanced === 1 ? "" : "s"} advanced to ${result.targetStageId}.`);
                        setOverrideIds("");
                        await load(selectedSummary.id);
                      } catch (error) {
                        setMessage(error instanceof Error ? error.message : "Advancement override failed.");
                      }
                    })()}>Advance selected</Button>
                  </div>
                )}
                <div className="yak-trails__audit-log">
                  <strong>RECENT AUDIT</strong>
                  {details.audit.slice(0, 5).map((entry) => (
                    <div key={entry.id}><b>{entry.action}</b><span>{entry.reason}</span><small>{entry.actorUserId}</small></div>
                  ))}
                </div>
              </section>
            )}
          </>
        ) : (
          <section className="yak-trails__admin-card yak-trails__draft-form">
            <header><div><small>VERSIONED DEFINITION</small><h3>Create tournament</h3></div><strong>DRAFT</strong></header>
            <label>Name<input value={name} onChange={(event) => setName(event.target.value)} /></label>
            <label>Starts<input type="datetime-local" value={startValue} onChange={(event) => setStartValue(event.target.value)} /></label>
            <div className="yak-trails__admin-field-row">
              <label>Mode<select value={mode} onChange={(event) => setMode(event.target.value as "ffa" | "team")}><option value="ffa">FFA</option><option value="team">Red vs Blue</option></select></label>
              <label>Pool<select value={scope} onChange={(event) => setScope(event.target.value as "everyone" | "school")}><option value="everyone">Everyone</option><option value="school">School</option></select></label>
            </div>
            <label>Respawns<select value={respawnPolicy} onChange={(event) => setRespawnPolicy(event.target.value as typeof respawnPolicy)}><option value="elimination">One life</option><option value="immediate">Immediate</option><option value="delayed">2.5 second delay</option></select></label>
            {scope === "school" && <label>Allowed school IDs<input value={schoolIds} onChange={(event) => setSchoolIds(event.target.value)} placeholder="school-red, school-blue" /><small>One school creates an internal Red vs Blue event; two create school-vs-school teams.</small></label>}
            <label>Reason<input value={reason} onChange={(event) => setReason(event.target.value)} /></label>
            <label>Advanced JSON<textarea value={advancedJson} onChange={(event) => setAdvancedJson(event.target.value)} placeholder={JSON.stringify(visualDefinition, null, 2)} /><small>Optional complete definition for multi-stage DAGs, explicit seeds, placement points, or staff override advancement.</small></label>
            <div className="yak-trails__admin-actions"><Button variant="secondary" disabled={loading} onClick={() => void preview()}>Validate & preview</Button><Button disabled={loading || name.trim().length < 3} onClick={() => void create()}>Create draft</Button></div>
          </section>
        )}
      </div>
      {loading && <div className="yak-trails__admin-loading" role="status">Syncing tournament state…</div>}
      {message && <button type="button" className="yak-trails__toast" onClick={() => setMessage(undefined)}>{message}</button>}
    </div>
  );
}
